// Badge telemetry (knowledge/123 § Unread badges): windowed `unread_increment` counts
// and the two badge invariants. Ids, enums and counters only, never content.
import { reportUiFailure, reportUiKpi } from "../report-failure.js";

export type UnreadSurfaceKind = "channel" | "dm_human" | "dm_agent" | "thread";
export type UnreadSource = "live" | "reseed";
export type UnreadKeyKind =
  | "channel_id"
  | "user_id"
  | "cybo_id"
  | "agent_session_id"
  | "thread_root"
  | "unknown";

export interface UnreadRosters {
  channelIds: ReadonlySet<string>;
  memberIds: ReadonlySet<string>;
  cyboIds: ReadonlySet<string>;
  sessionIds: ReadonlySet<string>;
}

export function unreadKeyKind(key: string, rosters: UnreadRosters): UnreadKeyKind {
  if (rosters.channelIds.has(key)) return "channel_id";
  if (rosters.memberIds.has(key)) return "user_id";
  if (rosters.cyboIds.has(key)) return "cybo_id";
  if (rosters.sessionIds.has(key)) return "agent_session_id";
  return "unknown";
}

// The surface a DM-count key belongs to when no increment site named it (a reseed):
// the server counts a DM from a member or from an agent session / cybo.
export function dmSurfaceKind(kind: UnreadKeyKind): UnreadSurfaceKind {
  return kind === "user_id" ? "dm_human" : "dm_agent";
}

export interface ReseedRaise {
  key: string;
  surfaceKind: UnreadSurfaceKind;
  keyKind: UnreadKeyKind;
  count: number;
}

// What a reseed RAISED: unread the server reported that this client had not counted.
export function reseedRaises(
  prior: Readonly<Record<string, number>>,
  after: Readonly<Record<string, number>>,
  seeded: { mentionCounts?: Record<string, number>; dmCounts?: Record<string, number> },
  rosters: UnreadRosters,
): ReseedRaise[] {
  const out: ReseedRaise[] = [];
  const raised = (key: string) => (after[key] ?? 0) - (prior[key] ?? 0);
  for (const key of Object.keys(seeded.mentionCounts ?? {})) {
    const count = raised(key);
    if (count > 0) out.push({ key, surfaceKind: "channel", keyKind: "channel_id", count });
  }
  for (const key of Object.keys(seeded.dmCounts ?? {})) {
    const count = raised(key);
    const keyKind = unreadKeyKind(key, rosters);
    if (count > 0) out.push({ key, surfaceKind: dmSurfaceKind(keyKind), keyKind, count });
  }
  return out;
}

export interface UnreadIncrement {
  workspaceId: string;
  surfaceKind: UnreadSurfaceKind;
  source: UnreadSource;
  keyKind: UnreadKeyKind;
}

type Fields = Record<string, string | number>;

export const UNREAD_INCREMENT_WINDOW_MS = 5 * 60_000;
// Bounds the per-key surface memory the invariant reads (one entry per badged key).
const SURFACE_MEMORY_MAX = 2_000;

function sendIncrement(fields: Fields): void {
  reportUiKpi("unread.increment", fields);
}

function surfaceKey(workspaceId: string, key: string): string {
  return `${workspaceId}\u0000${key}`;
}

// Sums increments per (workspace, surface, source, key kind) and sends one report per
// bucket per window: a busy channel costs one event per window, not one per message.
export class UnreadIncrementTelemetry {
  private readonly buckets = new Map<string, { props: UnreadIncrement; count: number }>();
  private readonly surfaces = new Map<string, UnreadSurfaceKind>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private windowStart: number;

  constructor(
    private readonly send: (fields: Fields) => void = sendIncrement,
    private readonly now: () => number = () => Date.now(),
    private readonly windowMs: number = UNREAD_INCREMENT_WINDOW_MS,
  ) {
    this.windowStart = now();
  }

  record(event: UnreadIncrement, key: string, count = 1): void {
    if (count <= 0) return;
    if (this.surfaces.size >= SURFACE_MEMORY_MAX) this.surfaces.clear();
    this.surfaces.set(surfaceKey(event.workspaceId, key), event.surfaceKind);
    const id = `${event.workspaceId}|${event.surfaceKind}|${event.source}|${event.keyKind}`;
    const bucket = this.buckets.get(id);
    if (bucket) bucket.count += count;
    else this.buckets.set(id, { props: event, count });
    this.timer ??= setTimeout(() => this.flush(), this.windowMs);
  }

  // The surface the last increment of `key` declared, for the invariant report.
  surfaceOf(workspaceId: string, key: string): UnreadSurfaceKind | undefined {
    return this.surfaces.get(surfaceKey(workspaceId, key));
  }

  flush(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
    const windowMs = this.now() - this.windowStart;
    this.windowStart = this.now();
    for (const { props, count } of this.buckets.values()) {
      this.send({
        workspaceId: props.workspaceId,
        surface_kind: props.surfaceKind,
        increment_source: props.source,
        key_kind: props.keyKind,
        count,
        window_ms: windowMs,
      });
    }
    this.buckets.clear();
  }
}

export interface UnreadRowCheck {
  workspaceId: string;
  // The workspace's raw count map (canonical key → count), as the dock/rail sum it.
  counts: Readonly<Record<string, number>>;
  isMuted(key: string): boolean;
  // A key whose door is elsewhere by design (an agent DM Activity hosts) is not a row's.
  hasOtherDoor(key: string): boolean;
  // A key nothing can open still goes out as a key without a row, but the badge does not count
  // it, so it stays out of the total the rows are compared with.
  isDoorless(key: string): boolean;
  // Canonical key → the number the sidebar row reading it shows.
  rowCounts: ReadonlyMap<string, number>;
  keyKind(key: string): UnreadKeyKind;
  surfaceKind(key: string): UnreadSurfaceKind;
}

export const UNREAD_KEY_WITHOUT_ROW = "unread.invariant.key_without_row";
export const UNREAD_TOTAL_MISMATCH = "unread.invariant.total_mismatch";
// A roster re-read (healDmSurfaces) can take seconds to give a new peer its row.
const ROW_GRACE_MS = 10_000;
const MISMATCH_REPORT_INTERVAL_MS = 30 * 60_000;

// Faults, each only after the state has held for the grace period: keys without a row go out
// as ONE aggregated beacon per workspace per interval (count + a sample, never one per key:
// the client-log route rate-limits at 30/min), the total mismatch once per workspace per interval.
export class UnreadRowInvariants {
  private readonly reportedKeys = new Set<string>();
  private readonly orphanSince = new Map<string, number>();
  private readonly mismatchSince = new Map<string, number>();
  private readonly lastMismatchReport = new Map<string, number>();
  private readonly lastKeyReport = new Map<string, number>();

  constructor(
    private readonly report: (source: string, err: Error, ids: Fields) => void = reportUiFailure,
    private readonly now: () => number = () => Date.now(),
    private readonly graceMs: number = ROW_GRACE_MS,
    private readonly mismatchIntervalMs: number = MISMATCH_REPORT_INTERVAL_MS,
  ) {}

  // Returns the delay until a pending state is due for a re-check, if any.
  check(c: UnreadRowCheck): number | undefined {
    const now = this.now();
    let total = 0;
    let due: number | undefined;
    const pending = new Set<string>();
    const throttled = this.throttled(this.lastKeyReport, c.workspaceId, now);
    const fresh: [string, number][] = [];
    for (const [key, n] of Object.entries(c.counts)) {
      if (n <= 0 || c.isMuted(key)) continue;
      // Activity hosting a key excuses it from having a row, never from the sum when it has one.
      if (c.hasOtherDoor(key) && !c.rowCounts.has(key)) continue;
      if (!c.isDoorless(key)) total += n;
      if (c.rowCounts.has(key)) continue;
      const id = surfaceKey(c.workspaceId, key);
      pending.add(id);
      const since = this.orphanSince.get(id) ?? now;
      this.orphanSince.set(id, since);
      const wait = since + this.graceMs - now;
      if (wait > 0) due = Math.min(due ?? wait, wait);
      else if (!throttled && !this.reportedKeys.has(id)) fresh.push([key, n]);
    }
    if (fresh.length > 0) {
      this.lastKeyReport.set(c.workspaceId, now);
      for (const [key] of fresh) this.reportedKeys.add(surfaceKey(c.workspaceId, key));
      this.reportKeysWithoutRow(c, fresh);
    }
    for (const id of this.orphanSince.keys()) {
      if (id.startsWith(`${c.workspaceId}\u0000`) && !pending.has(id)) this.orphanSince.delete(id);
    }
    const mismatchWait = this.checkTotal(c, total, now);
    if (mismatchWait !== undefined) due = Math.min(due ?? mismatchWait, mismatchWait);
    return due;
  }

  private throttled(last: Map<string, number>, workspaceId: string, now: number): boolean {
    const at = last.get(workspaceId);
    return at !== undefined && now - at < this.mismatchIntervalMs;
  }

  private reportKeysWithoutRow(c: UnreadRowCheck, keys: [string, number][]): void {
    const kinds: Record<string, number> = {};
    for (const [key] of keys) kinds[c.keyKind(key)] = (kinds[c.keyKind(key)] ?? 0) + 1;
    keys.sort((x, y) => y[1] - x[1]);
    this.report(UNREAD_KEY_WITHOUT_ROW, new Error("unread count on keys no sidebar row reads"), {
      workspaceId: c.workspaceId,
      keys: keys.length,
      count: keys.reduce((sum, [, n]) => sum + n, 0),
      key_kinds: Object.entries(kinds)
        .map(([k, n]) => `${k}:${n}`)
        .join(","),
      unreadKeys: keys
        .slice(0, 5)
        .map(([k]) => k)
        .join(","),
      // The KPI's breakdown dimensions read the largest key's kinds.
      key_kind: c.keyKind(keys[0][0]),
      surface_kind: c.surfaceKind(keys[0][0]),
    });
  }

  private checkTotal(c: UnreadRowCheck, total: number, now: number): number | undefined {
    let rowsSum = 0;
    for (const n of c.rowCounts.values()) rowsSum += n;
    if (rowsSum === total) {
      this.mismatchSince.delete(c.workspaceId);
      return undefined;
    }
    const since = this.mismatchSince.get(c.workspaceId) ?? now;
    this.mismatchSince.set(c.workspaceId, since);
    const wait = since + this.graceMs - now;
    if (wait > 0) return wait;
    if (this.throttled(this.lastMismatchReport, c.workspaceId, now)) return undefined;
    this.lastMismatchReport.set(c.workspaceId, now);
    this.report(
      UNREAD_TOTAL_MISMATCH,
      new Error("dock badge total differs from the sum of the sidebar rows"),
      { workspaceId: c.workspaceId, total, rows_sum: rowsSum, diff: total - rowsSum },
    );
    return undefined;
  }
}
